import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export default function ActivityDurationSelect({ value, onChange }) {
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState({})
    const trigger = useRef(null)
    const menu = useRef(null)
    const options = [...new Set([15, 30, 45, 60, 90, 120, Number(value)])].filter(Boolean).sort((a, b) => a - b)
    useLayoutEffect(() => {
        if (!open) return
        const rect = trigger.current.getBoundingClientRect()
        const height = Math.min(options.length * 36 + 8, 224)
        setPosition({ left: Math.max(8, Math.min(rect.right - 176, window.innerWidth - 184)), top: rect.bottom + height + 6 > window.innerHeight ? Math.max(8, rect.top - height - 6) : rect.bottom + 6 })
        menu.current?.querySelector('[aria-selected="true"]')?.focus()
    }, [open, options.length])
    useEffect(() => {
        if (!open) return undefined
        const close = (e) => {
            if (!trigger.current?.contains(e.target) && !menu.current?.contains(e.target)) setOpen(false)
        }
        const reposition = (e) => { if (!menu.current?.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', close)
        window.addEventListener('scroll', reposition, true)
        window.addEventListener('resize', reposition)
        return () => {
            document.removeEventListener('mousedown', close)
            window.removeEventListener('scroll', reposition, true)
            window.removeEventListener('resize', reposition)
        }
    }, [open])
    const dismiss = () => { setOpen(false); trigger.current?.focus() }
    return <>
        <button ref={trigger} type="button" aria-label="Duration in minutes" aria-haspopup="listbox" aria-expanded={open}
            onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true) } }}
            style={{ width: 102, flex: 'none', height: 42, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 5, border: 0, background: 'transparent', borderRadius: 7, font: 'inherit', fontSize: 14, color: '#36413D', cursor: 'pointer' }}>
            {value} min <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        {open && createPortal(<div ref={menu} role="listbox" aria-label="Activity duration"
            onKeyDown={(e) => {
                if (e.key === 'Escape' || e.key === 'Tab') { if (e.key === 'Escape') e.preventDefault(); dismiss(); return }
                const rows = [...menu.current.querySelectorAll('[role="option"]')]
                const index = rows.indexOf(document.activeElement)
                if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
                    e.preventDefault()
                    rows[e.key === 'Home' ? 0 : e.key === 'End' ? rows.length - 1 : (index + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length]?.focus()
                }
            }}
            style={{ position: 'fixed', ...position, zIndex: 10060, width: 176, maxHeight: 224, overflowY: 'auto', padding: 4, background: '#fff', border: '1px solid #DCE2DF', borderRadius: 8, boxShadow: '0 8px 24px rgba(24,33,31,.12)' }}>
            {options.map((duration) => <button key={duration} type="button" role="option" aria-selected={duration === Number(value)}
                onClick={() => { onChange(duration); dismiss() }}
                className="hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: 36, padding: '0 10px', border: 0, borderRadius: 4, background: duration === Number(value) ? '#EFF5F2' : undefined, font: 'inherit', fontSize: 13, color: '#36413D', cursor: 'pointer' }}>
                {duration} min {duration === Number(value) && <span style={{ color: '#1F5C50' }}>✓</span>}
            </button>)}
        </div>, document.body)}
    </>
}
